import { defineStore } from "pinia";
import { request, requestBool } from "@/api-connectors/basicRequests";
import { useAuth } from "@/api-connectors/auth";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { clear, get } from 'idb-keyval';

import { useLocationStore } from "@/stores/locations";
import { useMessagingStore } from "@/stores/messaging";
import { SupplierStore, useSupplierStore } from "@/stores/suppliers";
import { usePersistentStore } from "@/stores/persistent";
import { ListStore, useListStore } from "@/stores/lists";
import { useNotificationStore } from "@/stores/notification";

import { getPreference, setPreference } from "@/functions/preferences";

import {
    MeResponse,
    NotificationPerm,
    UserPermissions,
    RegisterCredentials,
    NotificationPermSubElement
} from "@/types/user";
import { AuthCredentials, isInstanceOf } from "@/types/functions";
import { Optional } from "@/types/helper";
import { PushNotifications } from "@capacitor/push-notifications";

export interface UserStore {
    sessionInfo: MeResponse;
    notificationPerm: NotificationPerm;
    permission: UserPermissions;
    progression?: number;
    cleanedCompatibility: boolean;
}

const meResponseKeys: (keyof MeResponse)[] = ['firebaseToken', 'id', 'emailConfirmed', 'sessionToken', 'notificationSettings', 'email']

export const useUserStore = defineStore('user', {
    state: (): UserStore => {
        return {
            sessionInfo: new MeResponse(),
            notificationPerm: new NotificationPerm(),
            permission: new UserPermissions(),
            progression: 0,
            cleanedCompatibility: false
        }
    },
    actions: {
        async localToPinia(): Promise<void> {
            if (Capacitor.isNativePlatform()) {
                const appInfo = await App.getInfo()
                const versionNumber = await getPreference('versionNumber')

                if (versionNumber !== appInfo.build) {
                    await clear()
                    await setPreference('versionNumber', appInfo.build)
                }
            }

            // save user and persistent from preference to pinia
            const localUser = await getPreference("userStore")
            if (localUser) this.$patch(JSON.parse(localUser))

            const localPersistent = await getPreference("persistentStore")
            if (localPersistent) usePersistentStore().$patch(JSON.parse(localPersistent))

            this.progression = 0

            // save the rest from indexedDB to pinia
            const localLocations = await get("locationStore")
            if (localLocations) useLocationStore().$patch(JSON.parse(localLocations))

            const localMessaging = await get("messagingStore")
            if (localMessaging) useMessagingStore().$patch(JSON.parse(localMessaging))

            const localSuppliers = await get("supplierStore")
            if (localSuppliers) {
                const supplierStoreState: SupplierStore = JSON.parse(localSuppliers)

                if (typeof supplierStoreState.catalogFilter.category === 'string') supplierStoreState.catalogFilter.category = {}
                useSupplierStore().$patch(supplierStoreState)
            }

            const localLists = await get("listStore")
            if (localLists) {
                const listStoreState: ListStore = JSON.parse(localLists)

                if (typeof listStoreState.listFilter.category === 'string') listStoreState.listFilter.category = {}
                useListStore().$patch(listStoreState)
                if (!this.cleanedCompatibility) {
                    for (const list of useListStore().lists.data) {
                        if (list.compatibilityMode) useListStore().editList({ ...list, compatibilityMode: false })
                    }
                    this.cleanedCompatibility = true
                }
            }

            const localNotification = await get("notificationStore")
            if (localNotification) useNotificationStore().$patch(JSON.parse(localNotification))
        },

        async getMe(): Promise<void> {
            const response = await request<MeResponse | undefined>({
                method: 'GET',
                route: `me`,
            })

            if (response) this.$patch({ sessionInfo: response })

            const responseUser = await request<MeResponse | undefined>({
                method: 'GET',
                route: `user`,
            })

            if (responseUser) {
                this.$patch({
                    sessionInfo: responseUser,
                    notificationPerm: responseUser.notificationSettings
                })
            }
        },

        async registerUser(registerForm: RegisterCredentials, options?: AxiosRequestConfig): Promise<AxiosResponse | undefined> {
            const { authRequest } = useAuth()

            return await authRequest('user/register', registerForm, options)
        },

        async login(loginForm: AuthCredentials): Promise<AxiosResponse | undefined> {
            const { username, password } = loginForm
            const { authRequest } = useAuth()
            const response = await authRequest('auth/login', { username: username, password: password })

            if (this.sessionInfo && this.sessionInfo.firebaseToken) await this.editUser(this.sessionInfo)

            return response
        },

        async loginByToken(token: string): Promise<AxiosResponse | undefined> {
            const { authRequest } = useAuth()
            const response = await authRequest('auth/login', { grant_type: 'refresh_token', refresh_token: token })

            if (this.sessionInfo && this.sessionInfo.firebaseToken) await this.editUser(this.sessionInfo)

            return response
        },

        async logoutUser(): Promise<boolean> {
            const response = await requestBool({
                method: 'POST',
                route: 'auth/logout'
            }, true)

            await this.resetStores()

            return response
        },

        async editUser(userData: MeResponse | Optional<RegisterCredentials, 'email'> | { profilePictureId: string | null }): Promise<boolean> {
            if (isInstanceOf<MeResponse>(userData, meResponseKeys) && userData.firebaseToken && Object.keys(userData.firebaseToken).length === 0)
                delete userData.firebaseToken

            const requestBody: Optional<MeResponse, 'notificationSettings'> = JSON.parse(JSON.stringify(userData))
            delete requestBody.notificationSettings

            const response = await request({
                method: 'PATCH',
                route: 'user',
                body: requestBody
            }, true)

            if (!response) return false

            await this.getMe()

            return true
        },

        async changeEmail(newEmail: string, password: string): Promise<boolean> {
            return requestBool({
                method: 'PATCH',
                route: 'user/email',
                body: {
                    newEmail, password
                }
            }, true)
        },

        async changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
            return requestBool({
                method: 'PUT',
                route: 'user/pass',
                body: {
                    oldPassword,
                    newPassword
                }
            }, true)
        },

        async deleteUser(): Promise<boolean> {
            const response = await requestBool({
                method: 'DELETE',
                route: 'user'
            }, true)
            return response
        },

        async resendConfirmationMail(): Promise<boolean> {
            const response = await requestBool({
                method: 'POST',
                route: 'user/resendEmailConfirmation'
            }, true)
            return response
        },

        async setNotificationPerm(): Promise<boolean> {
            const { news, promotion, order, reminder, productPromotion } = this.notificationPerm
            return requestBool({
                method: 'PATCH',
                route: 'user',
                body: {
                    notificationSettings: {
                        news,
                        promotion,
                        order,
                        reminder,
                        productPromotion
                    }
                }
            })
        },

        async getLocationPermissions(locationId: string): Promise<boolean> {
            if (!locationId) return false
            const response = await request<UserPermissions>({
                method: 'GET',
                route: `locations/${locationId}/permissions`
            })

            if (!response) return false

            this.permission = response
            return true
        },

        createCredentials(): Optional<RegisterCredentials, 'email'> {
            const userCopy = JSON.parse(JSON.stringify(this.sessionInfo))

            //!!TODO: Check with Marius which keys are needed for user edit even though they are not edited

            delete userCopy.firebaseToken
            delete userCopy.notificationSettings
            delete userCopy.emailConfirmed
            delete userCopy.sessionToken

            return userCopy
        },

        async resetStores(): Promise<void> {
            this.$reset()
            useMessagingStore().$reset()
            useSupplierStore().$reset()
            useLocationStore().$reset()
            useListStore().$reset()
            useNotificationStore().$reset()
        }
    },
    getters: {
        hasAnyNotificationPermission(state) {
            let hasAnyPermissions = false
            let permissionKey: keyof NotificationPerm
            let subPermissionKey: keyof NotificationPermSubElement

            for (permissionKey in state.notificationPerm) {
                if (permissionKey === 'all') continue

                for (subPermissionKey in state.notificationPerm[permissionKey]?.general) {
                    if (state.notificationPerm[permissionKey]?.general[subPermissionKey]) {
                        hasAnyPermissions = true
                        break;
                    }
                }
                if (hasAnyPermissions) break;
            }
            return hasAnyPermissions
        }
    }
})
